// ════════════════════════════════════════════════════════════════════
// src/storeVisits/storeVisitInsights.js   (SVR Phase 3, Sprint 3.3 + 3.4)
//
// ADMIN "Insights" sub-tab — three panels over a rolling window:
//   1. RAG health   — overall green/amber/red mix + stores flagged
//   2. AM activity   — visits per area manager, with their RAG mix
//   3. Trends        — visits per period (week or month), stacked by RAG
//
// Period: Last 4/8/12 weeks OR Last 3/6/12 months (trends bucket to match).
// Filters (HO/admin): period, Area. Excel export via the SheetJS (XLSX)
// global admin.html already loads.
//
// Entry point:  window.renderVisitInsights()  → #visit-insights-root.
// Reads only the LIST endpoint (overall_rag, visited_by_name, entry_time).
// Counts submitted|reviewed only. Role-scoped via currentAdmin + AREA_STORE_MAP.
// ════════════════════════════════════════════════════════════════════

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import { coverAwareAreaStores } from '../shared/area-cover.js';   // v136.9: own area plus any area handed over for today
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
const RAG_COLOR = { green: '#10b981', amber: '#f59e0b', red: '#ef4444', none: '#9ca3af' };
const RAG_LABEL = { green: 'Green', amber: 'Amber', red: 'Red', none: 'No rating' };
const RAG_ORDER = ['green', 'amber', 'red', 'none'];

function getToken() { return adminToken(); }   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') return DB.workerUrl();
  return '';
}
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function scopedStores() {
  const u = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
  if (!u) return null;
  if (u.role === 'admin') return null;
  if (u.role === 'area_manager') {
    return (typeof AREA_STORE_MAP !== 'undefined') ? coverAwareAreaStores(u, AREA_STORE_MAP) : [];   // v136.9: + hand-overs
  }
  if (u.role === 'store_manager') return u.store ? [u.store] : [];
  return null;
}
function canPickArea() {
  return scopedStores() === null && typeof AREA_STORE_MAP !== 'undefined' && Object.keys(AREA_STORE_MAP).length > 1;
}
function areaOptions() {
  if (typeof AREA_STORE_MAP === 'undefined') return [];
  return Object.keys(AREA_STORE_MAP).sort(function (a, b) { return String(a).localeCompare(String(b)); });
}
// Label an area id by its active area MANAGER's name (falls back to the area
// name, then "Area <id>") — never shows the raw number. v124.0.
function amLabel(areaId) {
  var nm = (typeof getActiveAreaManagerName === 'function') ? getActiveAreaManagerName(areaId) : '';
  if (nm && nm !== 'Unassigned') return nm;
  var an = (typeof getAreaNameById === 'function') ? getAreaNameById(areaId) : '';
  return an || ('Area ' + areaId);
}

// ── Dates (Asia/Dubai) ────────────────────────────────────────────────────
function dubaiMonth(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit' }).slice(0, 7); }
  catch (e) { return String(iso).slice(0, 7); }
}
function currentDubaiMonth() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit' }).slice(0, 7);
}
function dubaiDateStr(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }); }
  catch (e) { return String(iso).slice(0, 10); }
}
function dubaiDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: 'short', year: 'numeric' }); }
  catch (e) { return String(iso).slice(0, 10); }
}
function addDays(ymd, n) { const d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
function weekStartOf(ymd) {
  const d = new Date(ymd + 'T00:00:00Z'); const dow = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow));
  return d.toISOString().slice(0, 10);
}
function shortMonthLabel(ym) {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const p = ym.split('-'); const y = +p[0], m = +p[1];
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short', year: '2-digit' });
}
function shortWeekLabel(ymd) {
  try { return new Date(ymd + 'T00:00:00Z').toLocaleDateString('en-GB', { timeZone: 'UTC', day: '2-digit', month: 'short' }); }
  catch (e) { return ymd; }
}

// last `count` month-keys (oldest→newest)
function lastMonths(count) {
  const cur = currentDubaiMonth(); let y = +cur.slice(0, 4), m = +cur.slice(5, 7);
  const out = [];
  for (let i = 0; i < count; i++) { out.unshift(y + '-' + String(m).padStart(2, '0')); m -= 1; if (m === 0) { m = 12; y -= 1; } }
  return out;
}
// last `count` week-start dates (oldest→newest)
function lastWeeks(count) {
  const out = []; let d = weekStartOf(new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }));
  for (let i = 0; i < count; i++) { out.unshift(d); d = addDays(d, -7); }
  return out;
}

function ragOf(v) {
  const r = (v && v.overall_rag) ? String(v.overall_rag).toLowerCase() : '';
  return (r === 'green' || r === 'amber' || r === 'red') ? r : 'none';
}

// ── Period model ──────────────────────────────────────────────────────────
let _all = [];
let _unit = 'month';   // 'month' | 'week'
let _count = 6;
let _area = '';
let _slide = false;    // compact slide view for screenshots
let _loaded = false;

function windowKeys() { return _unit === 'week' ? lastWeeks(_count) : lastMonths(_count); }
function bucketOf(iso) { return _unit === 'week' ? weekStartOf(dubaiDateStr(iso)) : dubaiMonth(iso); }
function bucketLabel(k) { return _unit === 'week' ? shortWeekLabel(k) : shortMonthLabel(k); }
function periodWord() { return _count + (_unit === 'week' ? ' weeks' : ' months'); }

async function fetchVisits() {
  const headers = { 'Authorization': 'Bearer ' + getToken() };
  const res = await fetch(getWorkerUrl() + '/api/store-visits', { cache: 'no-store', headers });
  if (!res.ok) throw new Error('visits ' + res.status);
  const rows = await res.json();
  const scope = scopedStores();
  _all = (rows || []).filter(function (v) {
    if (!v) return false;
    if (v.status !== 'submitted' && v.status !== 'reviewed') return false;
    if (scope && scope.indexOf(v.store) === -1) return false;
    return true;
  });
  _loaded = true;
}

function windowVisits() {
  const set = {}; windowKeys().forEach(function (k) { set[k] = true; });
  const inArea = (_area && typeof AREA_STORE_MAP !== 'undefined' && AREA_STORE_MAP[_area]) ? new Set(AREA_STORE_MAP[_area]) : null;
  return _all.filter(function (v) {
    if (!set[bucketOf(v.entry_time || v.created_at)]) return false;
    if (inArea && !inArea.has(v.store)) return false;
    return true;
  });
}

// ── Chart bits ──────────────────────────────────────────────────────────────
function stackedBar(counts, width) {
  const total = RAG_ORDER.reduce(function (s, k) { return s + (counts[k] || 0); }, 0);
  const w = width || '100%';
  if (!total) return '<div style="height:14px;width:' + w + ';background:var(--surface-3);border-radius:4px;"></div>';
  const segs = RAG_ORDER.filter(function (k) { return counts[k]; }).map(function (k) {
    return '<div title="' + RAG_LABEL[k] + ': ' + counts[k] + '" style="height:100%;width:' + ((counts[k] / total) * 100) + '%;background:' + RAG_COLOR[k] + ';"></div>';
  }).join('');
  return '<div style="display:flex;height:14px;width:' + w + ';border-radius:4px;overflow:hidden;">' + segs + '</div>';
}
function legend() {
  return '<div style="display:flex;gap:14px;flex-wrap:wrap;margin-top:8px;font-size:11px;color:var(--sub,#6b7280);">' +
    RAG_ORDER.map(function (k) {
      return '<span style="display:inline-flex;align-items:center;gap:5px;"><span style="width:10px;height:10px;border-radius:2px;background:' + RAG_COLOR[k] + ';display:inline-block;"></span>' + RAG_LABEL[k] + '</span>';
    }).join('') + '</div>';
}
function panel(title, inner) {
  return '<div style="background:var(--card);border:1px solid var(--border,#e5e7eb);border-radius:10px;padding:16px 18px;margin-bottom:16px;">' +
    '<div style="font-weight:700;font-size:14px;margin-bottom:12px;color:var(--text,#111);">' + escapeHtml(title) + '</div>' + inner + '</div>';
}

function ragHealthPanel(visits) {
  const counts = { green: 0, amber: 0, red: 0, none: 0 };
  visits.forEach(function (v) { counts[ragOf(v)]++; });
  const total = visits.length;
  const latestByStore = {};
  visits.forEach(function (v) {
    const t = new Date(v.entry_time || v.created_at).getTime() || 0;
    const cur = latestByStore[v.store];
    if (!cur || t > cur._t) latestByStore[v.store] = { rag: ragOf(v), date: v.entry_time || v.created_at, _t: t };
  });
  const flagged = Object.keys(latestByStore)
    .filter(function (s) { return latestByStore[s].rag === 'red' || latestByStore[s].rag === 'amber'; })
    .map(function (s) { return { store: s, rag: latestByStore[s].rag, date: latestByStore[s].date }; })
    .sort(function (a, b) { return (a.rag === 'red' ? 0 : 1) - (b.rag === 'red' ? 0 : 1) || String(a.store).localeCompare(String(b.store)); });

  // Store-level headline — the number AMs actually mean by "how many reds":
  // stores whose LATEST visit in the window is red/amber. The visit-mix bar
  // below counts VISITS, which reads much higher for a repeatedly-visited
  // struggling store — labelling both levels explicitly is the whole point.
  const redStores = flagged.filter(function (f) { return f.rag === 'red'; }).length;
  const amberStores = flagged.length - redStores;
  const nowLine = flagged.length
    ? 'Right now: <span style="color:' + RAG_COLOR.red + ';">' + redStores + ' store' + (redStores === 1 ? '' : 's') + ' red</span> · <span style="color:' + RAG_COLOR.amber + ';">' + amberStores + ' amber</span>'
    : 'Right now: <span style="color:' + RAG_COLOR.green + ';">no stores flagged</span> — every store\u2019s latest visit is green';

  let inner = '<div style="font-size:15px;font-weight:800;color:var(--text,#111);">' + nowLine + '</div>' +
    '<div style="font-size:11px;color:var(--sub,#9ca3af);margin-bottom:12px;">based on each store\u2019s latest visit in this period</div>' +
    '<div style="font-size:13px;color:var(--sub,#6b7280);margin-bottom:8px;">' + total + ' visit' + (total === 1 ? '' : 's') + ' in the last ' + periodWord() + '</div>' +
    stackedBar(counts) + legend() +
    '<div style="font-size:11px;color:var(--sub,#9ca3af);margin-top:6px;">Counts every visit in the period — a store visited three times counts three times.</div>' +
    '<div style="display:flex;gap:18px;flex-wrap:wrap;margin-top:14px;">' +
    RAG_ORDER.map(function (k) {
      const lbl = k === 'none' ? RAG_LABEL[k] : RAG_LABEL[k] + ' visits';
      return '<div><span style="font-size:20px;font-weight:800;color:' + RAG_COLOR[k] + ';">' + counts[k] + '</span><span style="font-size:12px;color:var(--sub,#6b7280);margin-left:5px;">' + lbl + '</span></div>';
    }).join('') + '</div>';
  if (flagged.length) {
    inner += '<div style="margin-top:16px;border-top:1px solid var(--border,#eee);padding-top:12px;">' +
      '<div style="font-weight:700;font-size:12px;color:var(--danger-deep);margin-bottom:6px;">Currently flagged (latest visit amber/red)</div>' +
      flagged.map(function (f) {
        return '<div style="display:flex;align-items:center;gap:8px;font-size:13px;padding:3px 0;"><span style="width:9px;height:9px;border-radius:50%;background:' + RAG_COLOR[f.rag] + ';display:inline-block;"></span><span style="font-weight:600;">' + escapeHtml(f.store) + '</span><span style="color:var(--sub,#9ca3af);">· ' + escapeHtml(dubaiDate(f.date)) + '</span></div>';
      }).join('') + '</div>';
  }
  return panel('RAG health', inner);
}

function amRows(visits) {
  const byAm = {};
  visits.forEach(function (v) {
    const name = (v.visited_by_name && String(v.visited_by_name).trim()) || '(unattributed)';
    if (!byAm[name]) byAm[name] = { name: name, total: 0, green: 0, amber: 0, red: 0, none: 0, last: 0 };
    const a = byAm[name]; a.total++; a[ragOf(v)]++;
    const t = new Date(v.entry_time || v.created_at).getTime() || 0; if (t > a.last) a.last = t;
  });
  return Object.keys(byAm).map(function (k) { return byAm[k]; }).sort(function (a, b) { return b.total - a.total || a.name.localeCompare(b.name); });
}
function amActivityPanel(visits) {
  const rows = amRows(visits);
  if (!rows.length) return panel('Area manager activity', '<div style="font-size:13px;color:var(--sub,#6b7280);">No visits in this window.</div>');
  const body = rows.map(function (a) {
    return '<tr><td style="font-weight:600;">' + escapeHtml(a.name) + '</td><td>' + a.total + '</td>' +
      '<td style="min-width:140px;">' + stackedBar({ green: a.green, amber: a.amber, red: a.red, none: a.none }, '120px') + '</td>' +
      '<td style="white-space:nowrap;color:var(--sub,#6b7280);">' + escapeHtml(a.last ? dubaiDate(new Date(a.last).toISOString()) : '—') + '</td></tr>';
  }).join('');
  return panel('Area manager activity',
    '<table class="data-table" style="width:100%;"><thead><tr><th>Visited by</th><th>Visits</th><th>RAG mix</th><th>Last visit</th></tr></thead><tbody>' + body + '</tbody></table>' + legend());
}

function trendBuckets(visits) {
  const keys = windowKeys();
  const buckets = {};
  keys.forEach(function (k) { buckets[k] = { green: 0, amber: 0, red: 0, none: 0, total: 0 }; });
  visits.forEach(function (v) { const k = bucketOf(v.entry_time || v.created_at); if (buckets[k]) { buckets[k][ragOf(v)]++; buckets[k].total++; } });
  return { keys: keys, buckets: buckets };
}
function trendsPanel(visits) {
  const tb = trendBuckets(visits);
  const max = Math.max(1, ...tb.keys.map(function (k) { return tb.buckets[k].total; }));
  const MAXH = 120;
  const bars = tb.keys.map(function (k) {
    const b = tb.buckets[k]; const colH = Math.round((b.total / max) * MAXH);
    const segs = RAG_ORDER.filter(function (kk) { return b[kk]; }).map(function (kk) {
      return '<div style="height:' + Math.round((b[kk] / (b.total || 1)) * colH) + 'px;background:' + RAG_COLOR[kk] + ';" title="' + RAG_LABEL[kk] + ': ' + b[kk] + '"></div>';
    }).join('');
    return '<div style="display:flex;flex-direction:column;align-items:center;gap:6px;flex:1;min-width:30px;">' +
      '<div style="font-size:11px;font-weight:700;color:var(--text,#111);">' + (b.total || '') + '</div>' +
      '<div style="display:flex;flex-direction:column-reverse;width:24px;height:' + MAXH + 'px;border-radius:4px 4px 0 0;overflow:hidden;background:var(--surface-3);">' + segs + '</div>' +
      '<div style="font-size:10px;color:var(--sub,#6b7280);white-space:nowrap;">' + escapeHtml(bucketLabel(k)) + '</div></div>';
  }).join('');
  return panel('Visits per ' + (_unit === 'week' ? 'week' : 'month'), '<div style="display:flex;align-items:flex-end;gap:6px;padding:4px 0;overflow-x:auto;">' + bars + '</div>' + legend());
}

// ── Render ──────────────────────────────────────────────────────────────────
function ctrlLabel(text, inner) {
  return '<label style="display:flex;flex-direction:column;gap:3px;font-size:11px;font-weight:700;color:var(--sub,#6b7280);text-transform:uppercase;letter-spacing:.4px;">' + text + inner + '</label>';
}
function periodBar() {
  const opts = [['w4', 'Last 4 weeks'], ['w8', 'Last 8 weeks'], ['w12', 'Last 12 weeks'], ['m3', 'Last 3 months'], ['m6', 'Last 6 months'], ['m12', 'Last 12 months']];
  const cur = (_unit === 'week' ? 'w' : 'm') + _count;
  let html = '<div style="display:flex;align-items:flex-end;gap:12px;margin-bottom:16px;flex-wrap:wrap;">';
  html += ctrlLabel('Period',
    '<select id="svr-insights-period" style="padding:6px 8px;border:1px solid var(--border,#d1d5db);border-radius:7px;font-size:13px;min-width:140px;">' +
    opts.map(function (o) { return '<option value="' + o[0] + '"' + (o[0] === cur ? ' selected' : '') + '>' + o[1] + '</option>'; }).join('') + '</select>');
  if (canPickArea()) {
    html += ctrlLabel('Area Manager',
      '<select id="svr-insights-area" style="padding:6px 8px;border:1px solid var(--border,#d1d5db);border-radius:7px;font-size:13px;min-width:150px;"><option value="">All area managers</option>' +
      areaOptions().map(function (a) { return '<option value="' + escapeHtml(a) + '"' + (a === _area ? ' selected' : '') + '>' + escapeHtml(amLabel(a)) + '</option>'; }).join('') + '</select>');
  }
  html += '<div style="margin-left:auto;display:flex;gap:8px;">' +
    '<button id="svr-insights-slide" class="btn ' + (_slide ? 'btn-green' : 'btn-outline') + '" style="padding:7px 12px;font-size:13px;">📊 Slide view</button>' +
    '<button id="svr-insights-export" class="btn btn-outline" style="padding:7px 12px;font-size:13px;">⬇ Excel</button>' +
  '</div>';
  html += '</div>';
  return html;
}

// Minimal top bar for slide view — set period/area in full view first.
function slideTopBar(title) {
  return '<div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;flex-wrap:wrap;">' +
    '<div style="font-size:15px;font-weight:800;color:var(--text,#111);">' + title + '</div>' +
    '<div style="margin-left:auto;display:flex;gap:8px;">' +
      '<button id="svr-insights-slide" class="btn btn-green" style="padding:7px 12px;font-size:13px;">📊 Slide view</button>' +
      '<button id="svr-insights-export" class="btn btn-outline" style="padding:7px 12px;font-size:13px;">⬇ Excel</button>' +
    '</div></div>';
}

// Compact AM panel (name + count only) — fits a narrow grid column.
function compactAmPanel(visits) {
  const rows = amRows(visits).slice(0, 8);
  if (!rows.length) return panel('Visits by area manager', '<div style="font-size:13px;color:var(--sub,#6b7280);">No visits.</div>');
  const body = rows.map(function (a) {
    return '<div style="display:flex;justify-content:space-between;font-size:13px;padding:4px 0;border-bottom:1px solid var(--border,#f0f0f0);">' +
      '<span style="font-weight:600;">' + escapeHtml(a.name) + '</span><span>' + a.total + '</span></div>';
  }).join('');
  return panel('Visits by area manager', body);
}

// Responsive grid that lays the three panels across laptop width, wraps below.
function compactGrid(visits) {
  return '<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:14px;align-items:start;">' +
    ragHealthPanel(visits) + trendsPanel(visits) + compactAmPanel(visits) +
  '</div>';
}

function renderView(root) {
  const visits = windowVisits();
  if (_slide) {
    const title = 'Insights — Last ' + escapeHtml(periodWord()) + ' · ' + escapeHtml(_area ? amLabel(_area) : 'All area managers');
    root.innerHTML = slideTopBar(title) + compactGrid(visits);
  } else {
    root.innerHTML = periodBar() + ragHealthPanel(visits) + amActivityPanel(visits) + trendsPanel(visits);
  }
  const sel = root.querySelector('#svr-insights-period');
  if (sel) sel.addEventListener('change', function () { _unit = sel.value[0] === 'w' ? 'week' : 'month'; _count = parseInt(sel.value.slice(1), 10) || 6; renderView(root); });
  const areaSel = root.querySelector('#svr-insights-area');
  if (areaSel) areaSel.addEventListener('change', function () { _area = areaSel.value || ''; renderView(root); });
  const slideBtn = root.querySelector('#svr-insights-slide');
  if (slideBtn) slideBtn.addEventListener('click', function () { _slide = !_slide; renderView(root); });
  const ex = root.querySelector('#svr-insights-export');
  if (ex) ex.addEventListener('click', function () { exportInsights(visits); });
}

// ── Excel export ──────────────────────────────────────────────────────────
function downloadXlsx(sheets, filename) {
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { ensureXlsx().then(function () { downloadXlsx(sheets, filename); }, function (e) { alert(e.message); }); return; }
  const wb = XLSX.utils.book_new();
  sheets.forEach(function (s) { XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s.aoa), String(s.name).slice(0, 31)); });
  XLSX.writeFile(wb, filename);
}
function safeTag(s) { return String(s || '').replace(/[^\w]+/g, '_').replace(/^_|_$/g, '') || 'all'; }

function exportInsights(visits) {
  const counts = { green: 0, amber: 0, red: 0, none: 0 };
  visits.forEach(function (v) { counts[ragOf(v)]++; });
  const summary = [
    ['ELC Store Visits — Insights'],
    ['Period', 'Last ' + periodWord()],
    ['Area Manager', _area ? amLabel(_area) : 'All area managers'],
    [],
    ['RAG', 'Visits'],
    ['Green', counts.green], ['Amber', counts.amber], ['Red', counts.red], ['No rating', counts.none],
    ['Total', visits.length],
  ];
  const am = [['Visited by', 'Visits', 'Green', 'Amber', 'Red', 'No rating', 'Last visit']];
  amRows(visits).forEach(function (a) { am.push([a.name, a.total, a.green, a.amber, a.red, a.none, a.last ? dubaiDate(new Date(a.last).toISOString()) : '']); });
  const tb = trendBuckets(visits);
  const tr = [[(_unit === 'week' ? 'Week' : 'Month'), 'Visits', 'Green', 'Amber', 'Red', 'No rating']];
  tb.keys.forEach(function (k) { const b = tb.buckets[k]; tr.push([bucketLabel(k), b.total, b.green, b.amber, b.red, b.none]); });
  downloadXlsx(
    [{ name: 'Summary', aoa: summary }, { name: 'Area manager activity', aoa: am }, { name: 'Trends', aoa: tr }],
    'ELC_Insights_' + (_unit === 'week' ? 'w' : 'm') + _count + '_' + safeTag(_area) + '.xlsx'
  );
}

async function renderVisitInsights() {
  const root = document.getElementById('visit-insights-root');
  if (!root) { console.warn('[storeVisitInsights] #visit-insights-root not found'); return; }
  root.innerHTML = '<div style="padding:28px 4px;color:var(--sub,#6b7280);font-size:14px;">Loading insights…</div>';
  try { await fetchVisits(); renderView(root); }
  catch (e) { root.innerHTML = '<div style="padding:28px 4px;color:var(--ui-bad);font-size:14px;">Couldn\u2019t load insights. ' + escapeHtml(e.message) + '</div>'; }
}

window.renderVisitInsights = renderVisitInsights;
window.reloadVisitInsights = renderVisitInsights;

export { renderVisitInsights };
